import { Controller, useFormContext } from 'react-hook-form'
import React, { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { CurecallCheckbox, CurecallInput, CurecallInputLabel } from '@honestica/curecall-ui'
import {
  EPermissionSpecialAuthorization,
  SPECIAL_AUTHORIZATIONS,
  specialAuthorizationToIntlTitle,
  TPermissionFormValues,
} from './permissions.type'

function PermissionHeadingFields() {
  const { t } = useTranslation()
  const { control } = useFormContext<TPermissionFormValues>()

  /**
   *
   * @param {EPermissionSpecialAuthorization} specialAuthorization The SpecialAuthorization that has been clicked
   * @param {EPermissionSpecialAuthorization[]} selectedSpecialAuthorizations Value containing all checked SpecialAuthorizations
   * @param {(_: (EPermissionSpecialAuthorization | undefined)[]) => void} onChange Function to call with new SpecialAuthorizations value
   * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void}
   */
  const handleChangeSpecialAuthorizations =
    (
      specialAuthorization: EPermissionSpecialAuthorization,
      selectedSpecialAuthorizations: EPermissionSpecialAuthorization[],
      onChange: (_: (EPermissionSpecialAuthorization | undefined)[]) => void,
    ): ((event: React.ChangeEvent<HTMLInputElement>) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      let newSelectedSpecialAuthorizationsValue
      if (event.target.checked) {
        // Add the newly checked SpecialAuthorization into the array. Ensure no duplicates
        newSelectedSpecialAuthorizationsValue = [
          ...new Set([...(selectedSpecialAuthorizations ?? []), specialAuthorization]),
        ]
      } else {
        // Pop the unchecked SpecialAuthorization from the array
        newSelectedSpecialAuthorizationsValue = selectedSpecialAuthorizations?.filter(
          (sa) => sa !== specialAuthorization,
        )
      }
      onChange(newSelectedSpecialAuthorizationsValue)
    }

  return (
    <div className="c-flex c-gap-8 c-flex-row">
      <div className="c-flex-1">
        <Controller
          control={control}
          name="name"
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <CurecallInput
              placeholder={t('pages.configuration.permissions.enter-permission-name')}
              label={t('pages.configuration.permissions.name')}
              value={value}
              onChange={onChange}
            />
          )}
        />
      </div>

      <div className="c-flex-1">
        <Controller
          control={control}
          name="specialAuthorizations"
          rules={{ required: false }}
          render={({ field: { onChange, value: selectedSpecialAuthorizations } }) => (
            <>
              <CurecallInputLabel label={t('pages.configuration.permissions.special-authorizations')} />
              {SPECIAL_AUTHORIZATIONS.map((specialAuthorization) => (
                <CurecallCheckbox
                  key={specialAuthorization}
                  label={t(specialAuthorizationToIntlTitle[specialAuthorization])}
                  onChange={handleChangeSpecialAuthorizations(
                    specialAuthorization,
                    selectedSpecialAuthorizations,
                    onChange,
                  )}
                  checked={selectedSpecialAuthorizations?.includes(specialAuthorization)}
                />
              ))}
            </>
          )}
        />
      </div>
    </div>
  )
}

export { PermissionHeadingFields }
